import {_} from 'lodash';
import {IBasicParams, IMessageData, IObjectData, IRuleset, TSyncfield} from "../../index";
import * as ko from "knockout";
import {createErrorMessage, getObjectDisplayName, getObjectType} from "../../util/misc";
import {global, lm} from "../../appContext";
import {REQUEST_MESSAGE} from "../../constants/Language";
import {ObjectTypeId} from "../../constants";
import {getHash, setRouteHash} from "../../constants/RouteDefinitions";
import {getSearchObjectParams} from "../../util/request";
import {nsTooltip} from "../../util/nsTooltip";
import {nsRuleset} from "../ruleset/nsRuleset";
import departmentHtml from './department.html?raw';

export let template: string = departmentHtml;

interface IParams extends Partial<IBasicParams> {
    id: number
    name?: string
    showIcon?: boolean
    linkable?: boolean
    target?: string
}

/** Load a single department via searchobject (first match by syncfield key/value). */
function fetchDepartmentBySearchObject(
    companyid: string,
    key: TSyncfield,
    value: string | number,
): Promise<IObjectData | null> {
    if (!value && value !== 0) {
        return Promise.resolve(null);
    }

    const ruleset: IRuleset = nsRuleset.generateRuleset({
        companyid: companyid,
        objecttype: getObjectType(ObjectTypeId.Department),
        rules: [nsRuleset.generateRule({
            argument: value,
            argumentoperator: 'Er lik',
            attribute: key,
        })],
    });

    return global.getPromiseRequest('searchobject')
        .request(getSearchObjectParams({
            deleted: 0,
            jruleset: JSON.stringify(ruleset),
            objecttypeid: ObjectTypeId.Department,
            orgnr: companyid,
            resultsprpage: 1,
            searchcols: 'Id',
            sortcol: 'Department',
        }))
        .then((data: IObjectData[]) => data[0] ?? null);
}

export class viewModel {
    private data!: IObjectData;
    private name: string | undefined;
    private orgnr: string | undefined;
    private readonly id: number;
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly text: KnockoutObservable<string | null> = ko.observable(null);
    private showIcon: boolean;
    private linkable: boolean;
    private target: string | null;

    constructor(protected params: IParams) {
        this.id = params.id;
        this.name = params.name
        this.orgnr = params.orgnr ?? global.login.selectedOrganizationId() ?? undefined;
        this.showIcon = this.params.showIcon ?? true;
        this.linkable = false; // it's not linkable until we've confirmed permission to department
        this.target = this.params.target ?? null

        if (this.id) {
            this.isReady(false);
            this.messageData({message: REQUEST_MESSAGE});
            fetchDepartmentBySearchObject(this.orgnr ?? '', 'Id', this.id)
                .then((data: IObjectData | null) => {
                    this.data = data!;
                    const hasPermissionToDepartment = !!this.data;
                    this.name = (this.name ?? (this.data ? getObjectDisplayName(this.data) : undefined)) || String(this.data?.id ?? this.id);
                    if (hasPermissionToDepartment) {
                        this.linkable = this.params.linkable ?? true
                    } else {
                        this.name = this.name ?? String(this.id)
                    }
                    this.messageData(null);
                    this.isReady(true);
                })
                .catch((reason: string) => {
                    if (!!this.name) {
                        this.messageData(null)
                        this.isReady(true)
                        return
                    }
                    this.messageData(createErrorMessage(String(reason)))
                });
        }
    }

    private onPrepareClick() {
        const paramsOrgnr = this.params.orgnr;
        const selectedOrgnr = global.login.selectedOrganizationId();
        const differentOrganization = (paramsOrgnr ?? selectedOrgnr) != selectedOrgnr;
        if (differentOrganization && paramsOrgnr) {
            global.login.setSelectedOrganizationById(paramsOrgnr);
            setRouteHash('departmentdetails', {id: this.params.id, orgnr: paramsOrgnr});
            return false;
        }
        return true;
    }

    private initAnchorBindings() {
        return _.defaultsDeep({
            attr: {href: getHash('departmentdetails', {id: this.data.id}, true, this.orgnr), target: this.target},
            click: () => this.onPrepareClick(),
        }, this.initBindings())
    }

    private initBindings() {
        return {
            text: this.name,
            attr: {title: this.data ? (lm.t(nsTooltip.getTitle_objectdata(this.data) ?? '') ?? '') : ''}
        }
    }

}
